import React, { memo, useEffect, useRef } from "react";
import {
  buildPdfUrlWithPage,
  usePdfNavigation,
} from "#components/DocumentViewer/context/PdfNavigationContext";
import * as styles from "../styles_vanilla.css";

type PdfPanelProps = {
  url: string;
  title?: string;
  onRefreshUrl?: () => void;
};

// Refresh this far ahead of the URL's actual expiration so the iframe never
// requests an expired URL.
const REFRESH_BUFFER_MS = 60 * 1000;

// Used only when the URL's expiration cannot be parsed. Kept conservative
// because some pre-signed URLs may have a shorter TTL than the S3 default.
const FALLBACK_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const parseNonNegativeInt = (value: string | null): number | null => {
  if (value === null) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

/**
 * Compute milliseconds until a pre-signed S3 URL expires. Supports both
 * URL signature formats:
 * 1. Signature V2: `Expires` (Unix timestamp in seconds)
 * 2. Signature V4: `X-Amz-Date` + `X-Amz-Expires`
 *
 * Returns the number of milliseconds until expiration (negative if the URL
 * has already expired), or `null` if the URL does not contain parseable
 * expiration information.
 */
export const getMsUntilUrlExpiration = (url: string): number | null => {
  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    return null;
  }
  const params = urlObj.searchParams;

  const expiresSeconds = parseNonNegativeInt(params.get("Expires"));
  if (expiresSeconds !== null) {
    return expiresSeconds * 1000 - Date.now();
  }

  const amzDate = params.get("X-Amz-Date");
  const amzExpiresSeconds = parseNonNegativeInt(params.get("X-Amz-Expires"));
  if (!amzDate || amzExpiresSeconds === null) return null;

  // `X-Amz-Date` uses ISO 8601 basic format: YYYYMMDDTHHMMSSZ.
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(amzDate);
  if (!match) return null;

  const [, yearStr, monthStr, dayStr, hourStr, minuteStr, secondStr] = match;
  const createdMs = Date.UTC(
    Number.parseInt(yearStr, 10),
    Number.parseInt(monthStr, 10) - 1, // JS months are 0-indexed.
    Number.parseInt(dayStr, 10),
    Number.parseInt(hourStr, 10),
    Number.parseInt(minuteStr, 10),
    Number.parseInt(secondStr, 10),
  );

  return createdMs + amzExpiresSeconds * 1000 - Date.now();
};

const PdfPanelComponent = ({
  url,
  title = "PDF Document",
  onRefreshUrl,
}: PdfPanelProps): JSX.Element => {
  const { targetPage } = usePdfNavigation();
  const pdfUrl = buildPdfUrlWithPage(url, targetPage);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stash the latest callback in a ref so the scheduling effect only depends
  // on `url`, not on the callback's identity. Without this, an inline arrow
  // from the parent (a new reference each render) would re-arm the timer on
  // every parent re-render.
  const onRefreshUrlRef = useRef(onRefreshUrl);
  useEffect(() => {
    onRefreshUrlRef.current = onRefreshUrl;
  }, [onRefreshUrl]);

  // Re-arm only when the URL changes or when a callback first becomes
  // available (or is removed).
  const hasRefreshCallback = onRefreshUrl !== undefined;

  useEffect(() => {
    if (!hasRefreshCallback) return undefined;

    const msUntilExpiration = getMsUntilUrlExpiration(url);
    let delayMs: number;
    if (msUntilExpiration === null) {
      delayMs = FALLBACK_REFRESH_INTERVAL_MS;
    } else if (msUntilExpiration <= REFRESH_BUFFER_MS) {
      // URL has already expired (or is about to) — refresh on the next tick
      // rather than waiting the fallback interval.
      delayMs = 0;
    } else {
      delayMs = msUntilExpiration - REFRESH_BUFFER_MS;
    }

    refreshTimerRef.current = setTimeout(() => {
      onRefreshUrlRef.current?.();
    }, delayMs);

    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [url, hasRefreshCallback]);

  return (
    <div className={styles.pdfPanel}>
      <iframe
        key={targetPage ?? "initial"}
        src={pdfUrl}
        className={styles.pdfIframe}
        title={title}
      />
    </div>
  );
};

export const PdfPanel = memo(PdfPanelComponent);
